<template>
  <div class="modern-form-container">
    <!-- Main Content Card -->
    <div class="form-card">
      <div class="card-header">
        <div class="header-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            />
            <polyline points="14,2 14,8 20,8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10,9 9,9 8,9" />
          </svg>
        </div>
        <h2 class="form-title">
          {{ t('admin-ui.terms_and_conditions_of_use') }}
        </h2>
      </div>

      <div class="card-body">
        <!-- Terms and Conditions Content -->
        <div class="terms-container">
          <div
            ref="termsContent"
            class="terms-content"
            :class="{'terms-content--with-indicator': showScrollIndicator}"
            v-html="termsAndConditions"
            @scroll="updateScrollIndicator"
          />
          <div v-if="showScrollIndicator" class="scroll-indicator">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
            <span>{{
              t('admin-ui.scroll_to_read_the_full_terms_and_conditions')
            }}</span>
          </div>
        </div>

        <!-- Acceptance Section -->
        <div class="acceptance-section">
          <div class="checkbox-container">
            <label class="custom-checkbox">
              <input
                id="checkbox"
                v-model="checked"
                type="checkbox"
                class="checkbox-input"
              />
              <span class="checkbox-custom">
                <svg
                  v-if="checked"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  class="check-icon"
                >
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              </span>
              <span class="checkbox-label">
                {{ t('admin-ui.text_accept_agreement') }}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Banner -->
    <div v-if="showBanner" class="banner-container">
      <hit-banner :show-animation="showAnimationBanner" @close="closeBanner()">
        <template #dynamic-content>
          <h6>{{ textBanner }}</h6>
        </template>
      </hit-banner>
    </div>
  </div>
</template>

<script lang="ts">
import {
  HitButton,
  HitCard,
  HitDialog,
  HitForm,
  HitInput,
  HitPanel,
} from '@hit/components/src';
import HitBanner from '@hit/components/src/components/banner/HitBanner.vue';
import {defineComponent, PropType} from 'vue';
import {useI18n} from 'vue-i18n';
import TopBarNavigation from '../TopBarNavigation.vue';
import {MyUserCompany} from '../../objects/UserCompany';

export default defineComponent({
  name: 'RightsForm',
  components: {
    HitInput,
    HitForm,
    HitPanel,
    TopBarNavigation,
    HitDialog,
    HitCard,
    HitButton,
    HitBanner,
  },
  props: {
    myObject: {
      type: Object as PropType<MyUserCompany>,
      required: true,
    },
    triggerValidation: {
      type: Number,
      default: 0,
    },
    showBanner: {
      type: Boolean,
      default: false,
    },
    textBanner: {
      type: String,
      default: '',
    },
    showAnimationBanner: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['nextClick', 'previousClick', 'validationResult', 'close'],
  setup() {
    const {t, locale} = useI18n();
    return {t, locale};
  },
  data() {
    return {
      // checked: ref(false),
      objectFormData: {
        vatNumber: null as string | null,
        designationLegal: null as string | null,
        street: null as string | null,
        houseNumber: null as string | null,
        boxNumber: null as string | null,
        postCode: null as string | null,
        city: null as string | null,
        billingEmail: null as string | null,
        confirmBillingEmail: null as string | null,
        phone: null as string | null,
        mobile: null as string | null,
        userLanguageSystem: null as string | null,
        activity: null as string | null,
      },
      checked: false,
      mobileView: false,
      termsAndConditions: '',
      showScrollIndicator: false,
    };
  },
  computed: {
    getRightState(): boolean {
      return this.checked;
    },
  },
  watch: {
    locale() {
      this.loadTermsAndConditions();
    },
    triggerValidation(newVal, oldVal) {
      if (newVal > oldVal) {
        this.$nextTick(() => {
          this.validateAndContinue();
        });
      }
    },
    termsAndConditions() {
      this.$nextTick(() => {
        this.updateScrollIndicator();
      });
    },
  },
  mounted() {
    this.loadTermsAndConditions();

    // Initialize checkbox with the value from myObject.rightsAccepted
    this.checked = this.myObject.rightsAccepted || false;

    // Batch data updates to minimize reactive triggers
    Object.assign(this.objectFormData, {
      userLanguageSystem: this.myObject.userSystemLanguage || null,
      vatNumber: this.myObject.vatNumber || null,
      designationLegal: this.myObject.designationLegal || null,
      street: this.myObject.street || null,
      houseNumber: this.myObject.houseNumber || null,
      boxNumber: this.myObject.boxNumber || null,
      postCode: this.myObject.postCode || null,
      city: this.myObject.city || null,
      billingEmail: this.myObject.billingEmail || null,
      confirmBillingEmail: this.myObject.billingEmail || null,
      phone: this.myObject.phone || null,
      mobile: this.myObject.mobile || null,
      activity: this.myObject.activity || null,
    });
  },
  methods: {
    loadTermsAndConditions() {
      const locale = String(this.locale).split('-')[0];
      const filePath = `/terms_and_conditions/${locale}.html`;

      fetch(filePath)
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Error during terms and conditions file retrieving: ${response.statusText}`,
            );
          }
          return response.text();
        })
        .then((data) => {
          this.termsAndConditions = data;
          this.$nextTick(() => {
            this.updateScrollIndicator();
          });
        })
        .catch((error) =>
          console.error('Error during terms and conditions file fetch:', error),
        );
    },
    validateAndContinue() {
      // Check if rights are accepted
      if (!this.checked) {
        this.$emit('validationResult', {
          success: false,
          message:
            'Veuillez accepter les conditions générales en cochant la case.',
        });
        return;
      }

      // All validation passed, emit nextClick to trigger actual form submission
      this.$emit('nextClick', this.checked);
      this.$emit('validationResult', {success: true});
    },
    closeBanner() {
      this.$emit('close');
    },
    updateScrollIndicator() {
      const el = this.$refs.termsContent as HTMLElement | undefined;
      if (!el) {
        this.showScrollIndicator = false;
        return;
      }

      const isScrollable = el.scrollHeight > el.clientHeight + 1;
      const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      this.showScrollIndicator = isScrollable && !isAtBottom;
    },
  },
});
</script>

<style scoped>
.modern-form-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 3rem 1rem 1rem 1rem;
  background: #f1f5f9;
  position: relative;
}

.form-card {
  background: #ffffff;
  border-radius: 1rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 0;
  max-width: 600px;
  width: 100%;
  overflow: hidden;
  position: relative;
  border: 1px solid #e5e7eb;
}

.card-header {
  background: #0d3749;
  color: white;
  padding: 1.5rem;
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  margin-bottom: 0.75rem;
}

.form-title {
  font-size: 1.375rem;
  font-weight: 600;
  margin: 0 0 0.375rem 0;
  line-height: 1.3;
}

.form-subtitle {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
}

.card-body {
  padding: 1.5rem;
}

.terms-container {
  position: relative;
  background: #ffffff;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.25rem;
  overflow: hidden;
}

.terms-content {
  height: 400px;
  overflow-y: auto;
  padding: 1.25rem;
  line-height: 1.5;
  color: #374151;
  font-size: 0.875rem;
  font-family:
    Open Sans,
    sans-serif;
}

.terms-content--with-indicator {
  padding-bottom: 3.5rem;
}

/* Styling for terms content */
:deep(.terms-content h1),
:deep(.terms-content h2),
:deep(.terms-content h3) {
  color: #0d3749;
  font-weight: 600;
  margin: 1.25rem 0 0.75rem 0;
}

:deep(.terms-content h1) {
  font-size: 1.125rem;
}

:deep(.terms-content h2) {
  font-size: 1rem;
}

:deep(.terms-content h3) {
  font-size: 0.9375rem;
}

:deep(.terms-content p) {
  margin: 0.75rem 0;
  text-align: justify;
}

:deep(.terms-content ul),
:deep(.terms-content ol) {
  margin: 0.75rem 0;
  padding-left: 1.25rem;
}

:deep(.terms-content li) {
  margin: 0.375rem 0;
}

:deep(.terms-content strong) {
  color: #0d3749;
  font-weight: 600;
}

.scroll-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(255, 255, 255, 0.95));
  padding: 2rem 1.5rem 1rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.terms-content::-webkit-scrollbar {
  width: 6px;
}

.terms-content::-webkit-scrollbar-track {
  background: #f9fafb;
  border-radius: 3px;
}

.terms-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.terms-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.acceptance-section {
  background: rgba(13, 55, 73, 0.05);
  border-radius: 1rem;
  padding: 1.5rem;
  border: 2px solid rgba(13, 55, 73, 0.1);
}

.checkbox-container {
  margin-bottom: 0;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.checkbox-custom {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 2px solid #0d3749;
  border-radius: 6px;
  background: #ffffff;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.checkbox-input:checked + .checkbox-custom {
  background: #0d3749;
  border-color: #0d3749;
  transform: scale(1.05);
}

.check-icon {
  color: white;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.2s ease;
}

.checkbox-input:checked + .checkbox-custom .check-icon {
  opacity: 1;
  transform: scale(1);
}

.checkbox-label {
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.5;
}

.custom-checkbox:hover .checkbox-custom {
  border-color: #1abc9c;
  transform: scale(1.05);
  box-shadow: 0 0 0 4px rgba(26, 188, 156, 0.1);
}

/* Responsive Design */
@media (min-width: 768px) {
  .modern-form-container {
    padding: 4rem 1rem 1rem 1rem;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .form-subtitle {
    font-size: 1rem;
  }

  .card-header {
    padding: 1.75rem;
  }

  .card-body {
    padding: 1.75rem;
  }

  .terms-content {
    height: 450px;
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.5rem 1rem 0.25rem 1rem;
    justify-content: center;
  }

  .form-card {
    max-width: 100%;
    border-radius: 0.75rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.25rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .terms-content {
    height: 350px;
    padding: 1rem;
  }

  .acceptance-section {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.25rem 0.75rem 0.125rem 0.75rem;
  }

  .form-card {
    border-radius: 0.5rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.125rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .terms-content {
    height: 300px;
    padding: 0.875rem;
  }

  .acceptance-section {
    padding: 0.875rem;
  }

  .custom-checkbox {
    gap: 0.625rem;
  }

  .scroll-indicator {
    padding: 1.25rem 0.875rem 0.5rem;
    font-size: 0.8125rem;
  }
}

/* Focus states and accessibility */
.checkbox-input:focus + .checkbox-custom {
  outline: 2px solid #0d3749;
  outline-offset: 2px;
}

/* Banner Container Styles */
.banner-container {
  margin-top: 1rem;
  max-width: 600px;
  width: 100%;
}
</style>
